import React, { useEffect, useState } from 'react';
import { api } from '../../services/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { FlaskConical, Plus, Trash2, Play, Save, RotateCcw, ArrowRight, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import CalendarView from './CalendarView';
import ConcentrationView from './ConcentrationView';

const fmt = (v) => (Number(v) || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const fmtDelta = (v) => {
  const n = Number(v) || 0;
  if (n === 0) return '0 €';
  const sign = n > 0 ? '+' : '';
  return sign + fmt(n);
};

const emptyScenario = () => ({
  _key: Math.random().toString(36).slice(2),
  invoice_id: '',
  // mode flags (what user is hypothesizing)
  change_modalita: false, modalita_pagamento: '',
  change_anticipo: false, anticipata: false,
  anticipo_banca: '', anticipo_percentuale: 80, anticipo_data_accredito: '',
  anticipo_commissioni: 0, anticipo_interessi: 0,
  movement_shifts: [], // [{movement_id, new_date}]
});

export default function SimulatorView({ invoices, enums, actualMovements, onApplied }) {
  const [scenarios, setScenarios] = useState([emptyScenario()]);
  const [simData, setSimData] = useState(null); // { simulated_movements, kpi, stress, scenarios }
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [view, setView] = useState('simulated'); // 'actual' | 'simulated'
  const [invMovements, setInvMovements] = useState({}); // map invoice_id → its current movements

  const openInvoices = invoices || [];

  // Auto-run simulation when scenarios change (debounced)
  useEffect(() => {
    const hasValid = scenarios.some(s => s.invoice_id);
    if (!hasValid) { setSimData(null); return; }
    const t = setTimeout(() => { runSimulation(); }, 400);
    return () => clearTimeout(t);
  }, [scenarios]);

  // Preload movements for invoices selected in scenarios (for shifts UI)
  useEffect(() => {
    const ids = scenarios.map(s => s.invoice_id).filter(Boolean);
    ids.forEach(async (id) => {
      if (invMovements[id]) return;
      try {
        const r = await api.get(`/treasury/invoices/${id}/movements`);
        setInvMovements(prev => ({ ...prev, [id]: r.data || [] }));
      } catch { /* ignore */ }
    });
  }, [scenarios.map(s => s.invoice_id).join('|')]);

  const buildPayload = () => {
    return scenarios.filter(s => s.invoice_id).map(s => {
      const out = { invoice_id: s.invoice_id };
      if (s.change_modalita && s.modalita_pagamento) out.modalita_pagamento = s.modalita_pagamento;
      if (s.change_anticipo) {
        out.anticipata = !!s.anticipata;
        if (s.anticipata) {
          out.anticipo_banca = s.anticipo_banca || null;
          out.anticipo_percentuale = Number(s.anticipo_percentuale) || 80;
          out.anticipo_data_accredito = s.anticipo_data_accredito || null;
          out.anticipo_commissioni = Number(s.anticipo_commissioni || 0);
          out.anticipo_interessi = Number(s.anticipo_interessi || 0);
        }
      }
      if (s.movement_shifts?.length) {
        out.movement_shifts = s.movement_shifts.filter(sh => sh.new_date).map(sh => ({
          movement_id: sh.movement_id, new_date: sh.new_date,
        }));
      }
      return out;
    });
  };

  const runSimulation = async () => {
    const payload = buildPayload();
    if (!payload.length) { setSimData(null); return; }
    setLoading(true);
    try {
      const r = await api.post('/treasury/simulate', { scenarios: payload });
      setSimData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore simulazione');
    } finally { setLoading(false); }
  };

  const applyScenarios = async () => {
    const payload = buildPayload();
    if (!payload.length) { toast.error('Nessuno scenario da applicare.'); return; }
    if (!window.confirm(`Applicare ${payload.length} scenari?\nLe scadenze aperte verranno sostituite secondo le simulazioni.`)) return;
    setApplying(true);
    try {
      for (const sc of payload) {
        const planBody = {};
        if (sc.modalita_pagamento) planBody.modalita_pagamento = sc.modalita_pagamento;
        if ('anticipata' in sc) {
          planBody.anticipata = sc.anticipata;
          if (sc.anticipata) {
            planBody.anticipo_banca = sc.anticipo_banca;
            planBody.anticipo_percentuale = sc.anticipo_percentuale;
            planBody.anticipo_data_accredito = sc.anticipo_data_accredito;
            planBody.anticipo_commissioni = sc.anticipo_commissioni;
            planBody.anticipo_interessi = sc.anticipo_interessi;
          }
        }
        if (Object.keys(planBody).length) {
          await api.put(`/treasury/invoices/${sc.invoice_id}/plan`, planBody);
        }
        if (sc.movement_shifts?.length) {
          for (const sh of sc.movement_shifts) {
            if (sh.new_date) {
              // Note: after plan-regenerate, original movement_ids may no longer exist;
              // shifts are best applied AFTER plan-regen on still-valid IDs only.
              try { await api.patch(`/treasury/movements/${sh.movement_id}`, { date: sh.new_date }); }
              catch { /* movement may have been regenerated; ignore */ }
            }
          }
        }
      }
      toast.success(`${payload.length} ${payload.length === 1 ? 'scenario applicato' : 'scenari applicati'}. Tesoreria aggiornata.`);
      setScenarios([emptyScenario()]);
      setSimData(null);
      onApplied && onApplied();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore applicazione');
    } finally { setApplying(false); }
  };

  const resetAll = () => {
    setScenarios([emptyScenario()]);
    setSimData(null);
    setView('simulated');
  };

  const updateSc = (idx, patch) => setScenarios(s => s.map((x, i) => i === idx ? { ...x, ...patch } : x));
  const addScenario = () => setScenarios(s => [...s, emptyScenario()]);
  const removeScenario = (idx) => setScenarios(s => s.length === 1 ? [emptyScenario()] : s.filter((_, i) => i !== idx));
  const addShift = (idx) => updateSc(idx, { movement_shifts: [...(scenarios[idx].movement_shifts || []), { movement_id: '', new_date: '' }] });
  const updShift = (scIdx, shIdx, patch) => {
    const next = scenarios[scIdx].movement_shifts.map((x, i) => i === shIdx ? { ...x, ...patch } : x);
    updateSc(scIdx, { movement_shifts: next });
  };
  const rmShift = (scIdx, shIdx) => {
    updateSc(scIdx, { movement_shifts: scenarios[scIdx].movement_shifts.filter((_, i) => i !== shIdx) });
  };

  const movementsForView = view === 'simulated' && simData ? simData.simulated_movements : actualMovements;

  return (
    <div className="space-y-4" data-testid="treasury-simulator-view">
      {/* Top: header + actions */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2"><FlaskConical className="w-5 h-5" />Simulatore What-If</h3>
          <p className="text-xs text-slate-500">Ipotizza modifiche al piano di pagamento e vedi l&apos;impatto su Calendario e Concentrazione, senza salvare nulla.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={resetAll} data-testid="sim-reset"><RotateCcw className="w-4 h-4 mr-1" />Reset</Button>
          <Button size="sm" onClick={applyScenarios} disabled={!simData || applying} data-testid="sim-apply"><Save className="w-4 h-4 mr-1" />Applica scenari</Button>
        </div>
      </div>

      {/* Scenario builder */}
      <Card><CardContent className="p-3 space-y-3">
        {scenarios.map((sc, idx) => {
          const inv = invoices.find(i => i.id === sc.invoice_id);
          const isClient = inv && (inv.direction === 'in' || inv.direction === 'other_in');
          const movs = invMovements[sc.invoice_id] || [];
          const openMovs = movs.filter(m => !m.paid_flag && !m.received_flag);
          return (
            <div key={sc._key} className="border-l-4 border-purple-400 bg-purple-50/30 rounded p-3 space-y-2" data-testid={`sim-scenario-${idx}`}>
              <div className="flex items-center gap-2">
                <Badge className="bg-purple-600 text-white">Scenario {idx + 1}</Badge>
                <div className="flex-1">
                  <Select value={sc.invoice_id} onValueChange={v => updateSc(idx, { invoice_id: v })}>
                    <SelectTrigger className="h-8" data-testid={`sim-invoice-${idx}`}><SelectValue placeholder="Seleziona una fattura…" /></SelectTrigger>
                    <SelectContent>
                      {openInvoices.map(i => (
                        <SelectItem key={i.id} value={i.id}>
                          {i.direction === 'in' ? '↑' : '↓'} {i.invoice_number || '(no n°)'} · {i.party_name || '—'} · {fmt(i.totale)} · {i.modalita_pagamento}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button size="sm" variant="ghost" onClick={() => removeScenario(idx)} title="Rimuovi scenario"><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>

              {sc.invoice_id && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                  {/* Modality */}
                  <div className="rounded border bg-white p-2 space-y-1.5">
                    <label className="flex items-center gap-2 font-semibold">
                      <Checkbox checked={sc.change_modalita} onCheckedChange={v => updateSc(idx, { change_modalita: !!v })} data-testid={`sim-mod-check-${idx}`} />
                      Cambia modalità
                    </label>
                    {sc.change_modalita && (
                      <Select value={sc.modalita_pagamento} onValueChange={v => updateSc(idx, { modalita_pagamento: v })}>
                        <SelectTrigger className="h-7" data-testid={`sim-mod-select-${idx}`}><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                        <SelectContent>
                          {(enums?.modalita_pagamento || []).filter(m => !['Rate personalizzate', 'Piano di rientro personalizzato'].includes(m)).map(m => (
                            <SelectItem key={m} value={m}>{m}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {/* Anticipo (clienti only) */}
                  <div className={`rounded border p-2 space-y-1.5 ${isClient ? 'bg-white' : 'bg-slate-100 opacity-50'}`}>
                    <label className="flex items-center gap-2 font-semibold">
                      <Checkbox disabled={!isClient} checked={sc.change_anticipo} onCheckedChange={v => updateSc(idx, { change_anticipo: !!v })} data-testid={`sim-ant-check-${idx}`} />
                      Anticipo bancario {!isClient && <span className="text-[9px] text-slate-400">(solo clienti)</span>}
                    </label>
                    {sc.change_anticipo && isClient && (
                      <>
                        <label className="flex items-center gap-1 text-[10px]">
                          <Checkbox checked={sc.anticipata} onCheckedChange={v => updateSc(idx, { anticipata: !!v })} />
                          Attiva anticipo
                        </label>
                        {sc.anticipata && (
                          <div className="grid grid-cols-2 gap-1">
                            <Input placeholder="Banca" value={sc.anticipo_banca} onChange={e => updateSc(idx, { anticipo_banca: e.target.value })} className="h-6 text-[10px]" />
                            <Input type="number" placeholder="% Anticipo" value={sc.anticipo_percentuale} onChange={e => updateSc(idx, { anticipo_percentuale: e.target.value })} className="h-6 text-[10px]" />
                            <Input type="date" value={sc.anticipo_data_accredito} onChange={e => updateSc(idx, { anticipo_data_accredito: e.target.value })} className="h-6 text-[10px] col-span-2" />
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Movement shifts */}
                  <div className="rounded border bg-white p-2 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold">Posticipa scadenze</p>
                      <Button size="sm" variant="ghost" className="h-6 px-1" onClick={() => addShift(idx)} disabled={!openMovs.length} data-testid={`sim-add-shift-${idx}`}><Plus className="w-3 h-3" /></Button>
                    </div>
                    {(sc.movement_shifts || []).map((sh, i) => (
                      <div key={`shift-${idx}-${sh.movement_id || 'na'}-${i}`} className="flex items-center gap-1">
                        <Select value={sh.movement_id} onValueChange={v => updShift(idx, i, { movement_id: v })}>
                          <SelectTrigger className="h-6 text-[10px] flex-1"><SelectValue placeholder="Scegli scadenza" /></SelectTrigger>
                          <SelectContent>
                            {openMovs.map(m => <SelectItem key={m.id} value={m.id}>{m.date} · {fmt(m.amount)}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <ArrowRight className="w-3 h-3 text-slate-400" />
                        <Input type="date" value={sh.new_date} onChange={e => updShift(idx, i, { new_date: e.target.value })} className="h-6 text-[10px]" />
                        <Button size="sm" variant="ghost" className="h-6 px-1" onClick={() => rmShift(idx, i)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                      </div>
                    ))}
                    {!openMovs.length && sc.invoice_id && <p className="text-[10px] text-slate-400">Nessuna scadenza aperta.</p>}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        <Button variant="outline" size="sm" onClick={addScenario} data-testid="sim-add-scenario"><Plus className="w-4 h-4 mr-1" />Aggiungi scenario</Button>
      </CardContent></Card>

      {/* Delta KPI */}
      {simData && (
        <Card><CardContent className="p-3" data-testid="sim-kpi-delta">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <DeltaKpi label="Δ Entrate 30gg" actual={simData.kpi.actual.entrate_30.amount} sim={simData.kpi.simulated.entrate_30.amount} positiveGood />
            <DeltaKpi label="Δ Entrate 90gg" actual={simData.kpi.actual.entrate_90.amount} sim={simData.kpi.simulated.entrate_90.amount} positiveGood />
            <DeltaKpi label="Δ Uscite 30gg" actual={simData.kpi.actual.uscite_30.amount} sim={simData.kpi.simulated.uscite_30.amount} />
            <DeltaStress label="Stress (rosso/arancio)" actual={simData.stress.actual.red + simData.stress.actual.orange} sim={simData.stress.simulated.red + simData.stress.simulated.orange} />
          </div>
        </CardContent></Card>
      )}

      {/* Toggle view */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="sim-view-toggle">
        <span className="text-xs text-slate-500">Visualizza:</span>
        <Button size="sm" variant={view === 'actual' ? 'default' : 'outline'} onClick={() => setView('actual')} data-testid="sim-view-actual">Attuale</Button>
        <Button size="sm" variant={view === 'simulated' ? 'default' : 'outline'} onClick={() => setView('simulated')} disabled={!simData} data-testid="sim-view-simulated">Simulato {simData && <Badge className="ml-1 bg-purple-100 text-purple-800 border-purple-300 text-[9px]">live</Badge>}</Button>
        {loading && <span className="text-[10px] text-slate-400">simulazione in corso…</span>}
      </div>

      {/* Calendar + Concentration with the chosen dataset */}
      <CalendarView movements={movementsForView} />
      <ConcentrationView movements={movementsForView} />
    </div>
  );
}

function DeltaKpi({ label, actual, sim, positiveGood }) {
  const delta = (sim || 0) - (actual || 0);
  const isPos = delta > 0, isNeg = delta < 0;
  const good = (positiveGood && isPos) || (!positiveGood && isNeg);
  const bad = (positiveGood && isNeg) || (!positiveGood && isPos);
  const color = delta === 0 ? 'text-slate-600' : good ? 'text-emerald-700' : bad ? 'text-red-700' : 'text-slate-600';
  const icon = delta === 0 ? null : isPos ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />;
  return (
    <div className="rounded border p-2">
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`text-base font-bold font-mono flex items-center gap-1 ${color}`}>{icon}{fmtDelta(delta)}</p>
      <p className="text-[9px] text-slate-400">attuale: {fmt(actual)} → sim: {fmt(sim)}</p>
    </div>
  );
}

function DeltaStress({ label, actual, sim }) {
  const delta = sim - actual;
  const good = delta < 0, bad = delta > 0;
  const color = delta === 0 ? 'text-slate-600' : good ? 'text-emerald-700' : 'text-red-700';
  const icon = delta === 0 ? <CheckCircle2 className="w-3 h-3" /> : good ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />;
  return (
    <div className="rounded border p-2">
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`text-base font-bold font-mono flex items-center gap-1 ${color}`}>{icon}{delta > 0 ? '+' : ''}{delta} sett.</p>
      <p className="text-[9px] text-slate-400">attuale: {actual} → sim: {sim}</p>
    </div>
  );
}
